import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

export type ProductType = 'cloud' | 'desktop' | 'mobile' | 'web';
export type ReleaseType = 'major' | 'minor' | 'patch' | 'security';
export type ReleaseStatus = 'draft' | 'scheduled' | 'published' | 'archived';
export type ChangeCategory = 'feature' | 'improvement' | 'fix' | 'breaking' | 'security';

export interface ReleaseArtifact {
  id: number;
  release_id: number;
  filename: string;
  original_name: string;
  file_size: number;
  mime_type: string;
  file_hash?: string | null;
  hash_algorithm?: string | null;
  os_type: string;
  architecture: string;
  min_os_version?: string | null;
  status: string;
  download_count: number;
  download_url?: string;
  created_at?: string;
}

export interface ReleaseChangeItem {
  id?: number;
  category: ChangeCategory;
  title: string;
  description?: string | null;
  badge?: string | null;
  media_url?: string | null;
  sort_order: number;
}

export interface PlatformRelease {
  id: number;
  product_id: number;
  version: string;
  version_code?: number | null;
  title: string;
  summary?: string | null;
  release_type: ReleaseType;
  status: ReleaseStatus;
  is_current: boolean;
  release_date: string;
  scheduled_at?: string | null;
  published_at?: string | null;
  system_requirements?: string | null;
  compatibility_notes?: string | null;
  upgrade_instructions?: string | null;
  meta?: Record<string, unknown> | null;
  created_by?: number | null;
  updated_by?: number | null;
  published_by?: number | null;
  created_at?: string;
  updated_at?: string;
  product?: PlatformProduct;
  changes?: ReleaseChangeItem[];
  artifacts?: ReleaseArtifact[];
  creator?: { id: number; name: string } | null;
  updater?: { id: number; name: string } | null;
  publisher?: { id: number; name: string } | null;
}

export interface PlatformProduct {
  id: number;
  slug: string;
  name: string;
  name_ar?: string | null;
  type: ProductType;
  description?: string | null;
  description_ar?: string | null;
  icon?: string | null;
  badge?: string | null;
  is_active: boolean;
  sort_order: number;
  website_url?: string | null;
  docs_url?: string | null;
  repo_url?: string | null;
  releases_count?: number;
  current_release?: PlatformRelease | null;
  created_at?: string;
  updated_at?: string;
}

export interface ReleaseListFilters {
  page?: number;
  per_page?: number;
  product_id?: number | string;
  status?: string;
  release_type?: string;
  search?: string;
}

export interface PaginatedReleases {
  data: PlatformRelease[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

// ---------------- PRODUCTS API ----------------

export async function fetchAdminProducts(): Promise<
  { ok: true; products: PlatformProduct[] } | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch('/platform/products');
  const out = await parseJsonResponse<PlatformProduct[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, products: Array.isArray(out.data) ? out.data : [] };
}

export async function createAdminProduct(payload: Partial<PlatformProduct>): Promise<
  { ok: true; product: PlatformProduct } | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch('/platform/products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformProduct>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, product: out.data as PlatformProduct };
}

export async function updateAdminProduct(
  id: number,
  payload: Partial<PlatformProduct>,
): Promise<{ ok: true; product: PlatformProduct } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/products/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformProduct>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, product: out.data as PlatformProduct };
}

export async function deleteAdminProduct(
  id: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/products/${id}`, { method: 'DELETE' });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

// ---------------- RELEASES API ----------------

export async function fetchAdminReleases(
  filters: ReleaseListFilters = {},
): Promise<
  { ok: true; list: PaginatedReleases } | { ok: false; message: string; unauthorized?: boolean }
> {
  const params = new URLSearchParams();
  if (filters.page) params.append('page', String(filters.page));
  if (filters.per_page) params.append('per_page', String(filters.per_page));
  if (filters.product_id) params.append('product_id', String(filters.product_id));
  if (filters.status) params.append('status', filters.status);
  if (filters.release_type) params.append('release_type', filters.release_type);
  if (filters.search) params.append('search', filters.search);

  const res = await apiFetch(`/platform/releases?${params.toString()}`);
  const out = await parseJsonResponse<PlatformRelease[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }

  const list: PaginatedReleases = {
    data: Array.isArray(out.data) ? out.data : [],
    current_page: out.meta?.current_page ?? 1,
    per_page: out.meta?.per_page ?? (filters.per_page || 15),
    total: out.meta?.total ?? (Array.isArray(out.data) ? out.data.length : 0),
    last_page: out.meta?.last_page ?? 1,
  };

  return { ok: true, list };
}

export async function fetchAdminRelease(
  id: number,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}`);
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function createAdminRelease(
  payload: Record<string, unknown>,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch('/platform/releases', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function updateAdminRelease(
  id: number,
  payload: Record<string, unknown>,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function deleteAdminRelease(
  id: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}`, { method: 'DELETE' });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

export async function publishAdminRelease(
  id: number,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}/publish`, { method: 'POST' });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function unpublishAdminRelease(
  id: number,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}/unpublish`, { method: 'POST' });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function scheduleAdminRelease(
  id: number,
  scheduled_at: string,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}/schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scheduled_at }),
  });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function archiveAdminRelease(
  id: number,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}/archive`, { method: 'POST' });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

export async function makeCurrentAdminRelease(
  id: number,
): Promise<{ ok: true; release: PlatformRelease } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${id}/make-current`, { method: 'POST' });
  const out = await parseJsonResponse<PlatformRelease>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, release: out.data as PlatformRelease };
}

// ---------------- ARTIFACTS API ----------------

export async function uploadReleaseArtifact(
  releaseId: number,
  formData: FormData,
): Promise<{ ok: true; artifact: ReleaseArtifact } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${releaseId}/artifacts`, {
    method: 'POST',
    body: formData,
  });
  const out = await parseJsonResponse<ReleaseArtifact>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, artifact: out.data as ReleaseArtifact };
}

export async function deleteReleaseArtifact(
  releaseId: number,
  artifactId: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/releases/${releaseId}/artifacts/${artifactId}`, {
    method: 'DELETE',
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}
